// ==================================
// Pagina Detalle de Administrador
// ==================================

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import {
  Button,
  Card,
  Badge,
  Modal,
  Input,
  Select,
  Spinner,
  ConfirmDialog,
} from '@/componentes/ui';
import { superadminsServicio } from '@/servicios';
import { useAuthStore } from '@/estado/authStore';
import type { PermisosSuperAdmin } from '@/tipos';

// Iconos
const IconVolver = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const IconEditar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconPassword = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const IconActivar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconDesactivar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const IconEliminar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconCancelar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const IconConfirmar = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const IconX = () => (
  <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function getEstadoBadgeVariant(
  estado: string
): 'success' | 'warning' | 'danger' | 'info' | 'gray' {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'gray'> = {
    activo: 'success',
    inactivo: 'gray',
    suspendido: 'danger',
  };
  return variants[estado] || 'gray';
}

const estadoOptions = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'suspendido', label: 'Suspendido' },
];

interface InfoItemProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}

function InfoItem({ label, value, icon }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
      {icon && (
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-stone-500 shadow-sm">
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">{label}</p>
        <div className="mt-0.5 text-sm font-medium text-obsidian-900">{value}</div>
      </div>
    </div>
  );
}

export function AdministradorDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usuario = useAuthStore((state) => state.usuario);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Edit form state
  const [editNombre, setEditNombre] = useState('');
  const [editApellidoPaterno, setEditApellidoPaterno] = useState('');
  const [editApellidoMaterno, setEditApellidoMaterno] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editTelefono, setEditTelefono] = useState('');
  const [editEstado, setEditEstado] = useState('');
  const [editPermisos, setEditPermisos] = useState<PermisosSuperAdmin>({
    crearVeterinarias: true,
    eliminarVeterinarias: false,
    modificarLicencias: false,
    verReportesGlobales: true,
    gestionarSuperAdmins: false,
  });

  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const { data: admin, isLoading } = useQuery({
    queryKey: ['superadmin', id],
    queryFn: () => superadminsServicio.obtenerPorId(id!),
    enabled: !!id,
  });

  const actualizarMutation = useMutation({
    mutationFn: () =>
      superadminsServicio.actualizar(id!, {
        nombre: editNombre,
        apellidoPaterno: editApellidoPaterno,
        apellidoMaterno: editApellidoMaterno || undefined,
        email: editEmail,
        telefono: editTelefono || undefined,
        estado: editEstado as 'activo' | 'inactivo' | 'suspendido',
        permisosSuperAdmin: editPermisos,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', id] });
      queryClient.invalidateQueries({ queryKey: ['superadmins'] });
      setShowEditModal(false);
      toast.success('Administrador actualizado');
    },
    onError: () => toast.error('Error al actualizar'),
  });

  const cambiarPasswordMutation = useMutation({
    mutationFn: () => superadminsServicio.cambiarPassword(id!, newPassword),
    onSuccess: () => {
      setShowPasswordModal(false);
      setNewPassword('');
      setPasswordError('');
      toast.success('Contraseña actualizada');
    },
    onError: () => toast.error('Error al cambiar la contraseña'),
  });

  const toggleEstadoMutation = useMutation({
    mutationFn: () => {
      const nuevoEstado = admin?.estado === 'activo' ? 'inactivo' : 'activo';
      return superadminsServicio.actualizar(id!, { estado: nuevoEstado });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', id] });
      queryClient.invalidateQueries({ queryKey: ['superadmins'] });
      toast.success(admin?.estado === 'activo' ? 'Administrador desactivado' : 'Administrador activado');
    },
    onError: () => toast.error('Error al cambiar el estado'),
  });

  const eliminarMutation = useMutation({
    mutationFn: () => superadminsServicio.eliminar(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmins'] });
      toast.success('Administrador eliminado');
      navigate('/administradores');
    },
    onError: () => toast.error('Error al eliminar el administrador'),
  });

  const openEditModal = () => {
    if (admin) {
      setEditNombre(admin.nombre);
      setEditApellidoPaterno(admin.apellidoPaterno);
      setEditApellidoMaterno(admin.apellidoMaterno || '');
      setEditEmail(admin.email);
      setEditTelefono(admin.telefono || '');
      setEditEstado(admin.estado);
      setEditPermisos(admin.permisosSuperAdmin || {
        crearVeterinarias: true,
        eliminarVeterinarias: false,
        modificarLicencias: false,
        verReportesGlobales: true,
        gestionarSuperAdmins: false,
      });
      setShowEditModal(true);
    }
  };

  const handlePasswordSubmit = () => {
    if (!newPassword.trim()) {
      setPasswordError('La contraseña es requerida');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setPasswordError('');
    cambiarPasswordMutation.mutate();
  };

  if (isLoading) {
    return (
      <div>
        <Header titulo="Cargando..." />
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (!admin) {
    return (
      <div>
        <Header titulo="Administrador no encontrado" />
        <div className="p-6">
          <Card className="text-center py-12">
            <p className="text-stone-500 mb-4">
              El administrador que buscas no existe
            </p>
            <Button leftIcon={<IconVolver />} onClick={() => navigate('/administradores')}>
              Volver a la lista
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const isSelf = admin.id === usuario?.id;

  const permisosLabels: { key: keyof PermisosSuperAdmin; label: string }[] = [
    { key: 'crearVeterinarias', label: 'Crear veterinarias' },
    { key: 'eliminarVeterinarias', label: 'Eliminar veterinarias' },
    { key: 'modificarLicencias', label: 'Modificar licencias' },
    { key: 'verReportesGlobales', label: 'Ver reportes globales' },
    { key: 'gestionarSuperAdmins', label: 'Gestionar super admins' },
  ];

  return (
    <div>
      <Header titulo={`${admin.nombre} ${admin.apellidoPaterno}`} />

      <div className="p-4 sm:p-6 space-y-6">
        {/* Header con info principal y acciones */}
        <Card>
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Info principal */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-sm">
                  {admin.nombre.charAt(0)}
                  {admin.apellidoPaterno.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-obsidian-900">
                    {admin.nombre} {admin.apellidoPaterno} {admin.apellidoMaterno || ''}
                  </h2>
                  <p className="text-sm text-stone-500">{admin.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant={getEstadoBadgeVariant(admin.estado)} dot>
                  {admin.estado}
                </Badge>
                <Badge variant="info">
                  {admin.rol}
                </Badge>
              </div>
            </div>

            {/* Acciones principales */}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" leftIcon={<IconVolver />} onClick={() => navigate('/administradores')}>
                Volver
              </Button>
              <Button variant="primary" leftIcon={<IconEditar />} onClick={openEditModal}>
                Editar
              </Button>
            </div>
          </div>
        </Card>

        {/* Grid de informacion */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna 1: Contacto */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contacto
            </h3>
            <div className="space-y-3">
              <InfoItem
                label="Email"
                value={admin.email}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
              />
              <InfoItem
                label="Teléfono"
                value={admin.telefono || 'Sin teléfono'}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
              />
            </div>
          </Card>

          {/* Columna 2: Seguridad */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Seguridad
            </h3>
            <div className="space-y-3">
              <InfoItem
                label="Último Acceso"
                value={
                  admin.ultimoAcceso
                    ? new Date(admin.ultimoAcceso).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Nunca'
                }
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              />
              <InfoItem
                label="Fecha de Registro"
                value={new Date(admin.createdAt).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
              />
            </div>
          </Card>

          {/* Columna 3: Permisos */}
          <Card>
            <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Permisos
            </h3>
            <div className="space-y-2">
              {permisosLabels.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                  {admin.permisosSuperAdmin?.[key] ? <IconCheck /> : <IconX />}
                  <span className="text-sm text-obsidian-900">{label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Acciones adicionales */}
        <Card>
          <h3 className="text-sm font-semibold text-obsidian-900 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            Acciones
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              leftIcon={<IconPassword />}
              onClick={() => {
                setNewPassword('');
                setPasswordError('');
                setShowPasswordModal(true);
              }}
            >
              Cambiar Contraseña
            </Button>

            {admin.estado === 'activo' ? (
              <Button
                variant="secondary"
                leftIcon={<IconDesactivar />}
                onClick={() => toggleEstadoMutation.mutate()}
                loading={toggleEstadoMutation.isPending}
              >
                Desactivar cuenta
              </Button>
            ) : (
              <Button
                variant="success"
                leftIcon={<IconActivar />}
                onClick={() => toggleEstadoMutation.mutate()}
                loading={toggleEstadoMutation.isPending}
              >
                Activar cuenta
              </Button>
            )}

            {!isSelf && (
              <Button
                variant="danger"
                leftIcon={<IconEliminar />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Eliminar
              </Button>
            )}
          </div>
        </Card>

        {/* Info de registro */}
        <div className="flex flex-wrap gap-4 text-sm text-stone-500">
          <span>
            Registrado: {new Date(admin.createdAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <span>
            Actualizado: {new Date(admin.updatedAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Modal para editar */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Administrador"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Nombre"
              value={editNombre}
              onChange={(e) => setEditNombre(e.target.value)}
              placeholder="Juan"
            />
            <Input
              label="Apellido Paterno"
              value={editApellidoPaterno}
              onChange={(e) => setEditApellidoPaterno(e.target.value)}
              placeholder="Pérez"
            />
          </div>
          <Input
            label="Apellido Materno"
            value={editApellidoMaterno}
            onChange={(e) => setEditApellidoMaterno(e.target.value)}
            placeholder="García (opcional)"
          />
          <Input
            label="Email"
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            placeholder="admin@alebrijecloud.com"
          />
          <Input
            label="Teléfono"
            value={editTelefono}
            onChange={(e) => setEditTelefono(e.target.value)}
            placeholder="55 1234 5678 (opcional)"
          />
          <Select
            label="Estado"
            options={estadoOptions}
            value={editEstado}
            onChange={(e) => setEditEstado(e.target.value)}
          />

          {/* Permisos */}
          <div>
            <p className="text-sm font-medium text-obsidian-900 mb-3">Permisos</p>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPermisos.crearVeterinarias}
                  onChange={(e) => setEditPermisos({ ...editPermisos, crearVeterinarias: e.target.checked })}
                  className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-obsidian-900">Crear veterinarias</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPermisos.eliminarVeterinarias}
                  onChange={(e) => setEditPermisos({ ...editPermisos, eliminarVeterinarias: e.target.checked })}
                  className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-obsidian-900">Eliminar veterinarias</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPermisos.modificarLicencias}
                  onChange={(e) => setEditPermisos({ ...editPermisos, modificarLicencias: e.target.checked })}
                  className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-obsidian-900">Modificar licencias</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPermisos.verReportesGlobales}
                  onChange={(e) => setEditPermisos({ ...editPermisos, verReportesGlobales: e.target.checked })}
                  className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-obsidian-900">Ver reportes globales</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editPermisos.gestionarSuperAdmins}
                  onChange={(e) => setEditPermisos({ ...editPermisos, gestionarSuperAdmins: e.target.checked })}
                  className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-obsidian-900">Gestionar super admins</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              leftIcon={<IconCancelar />}
              onClick={() => setShowEditModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<IconConfirmar />}
              onClick={() => actualizarMutation.mutate()}
              loading={actualizarMutation.isPending}
            >
              Guardar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para cambiar contrasena */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Cambiar Contraseña"
      >
        <div className="space-y-4">
          <p className="text-stone-600">
            Ingresa la nueva contraseña para {admin.nombre} {admin.apellidoPaterno}.
          </p>
          <Input
            label="Nueva Contraseña"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
            error={passwordError}
            placeholder="Mínimo 8 caracteres"
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
          />
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              leftIcon={<IconCancelar />}
              onClick={() => setShowPasswordModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<IconConfirmar />}
              onClick={handlePasswordSubmit}
              loading={cambiarPasswordMutation.isPending}
            >
              Cambiar Contraseña
            </Button>
          </div>
        </div>
      </Modal>

      {/* Dialogo de confirmacion para eliminar */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => eliminarMutation.mutate()}
        title="Eliminar administrador"
        message={
          <>
            <p>
              ¿Estás seguro de que deseas eliminar al administrador{' '}
              <strong>{admin.nombre} {admin.apellidoPaterno}</strong>?
            </p>
            <p className="mt-2 text-red-600 font-medium">
              Esta acción no se puede deshacer.
            </p>
          </>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
        loading={eliminarMutation.isPending}
      />
    </div>
  );
}

export default AdministradorDetalle;
